import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  // FolderShared,
  FilterAlt,
  RotateLeft,
  Downloading,
  FormatListBulleted,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import "dayjs/locale/pt";
import { useNavigate } from "react-router-dom";
import auth from "../services/auth";
import { getLogs } from "../services/source";

const LOGS_PAGE_SIZE = 40;

dayjs.locale("pt");

const ACTION_LABELS_PT = {
  login: "Login",
  create: "Criar",
  edit: "Editar",
  upload: "Carregar",
  delete: "Apagar",
};

const ENTITY_LABELS_PT = {
  user: "Utilizador",
  client: "Cliente",
  document: "Documento",
  contract: "Contrato",
  service: "Serviço",
  company_access: "Acesso da empresa",
  client_access: "Acesso do cliente",
};

const ACTION_CHIP_COLOR = {
  login: "warning",
  delete: "error",
  edit: "success",
};

const formatDateTimePt = (value) => {
  if (!value) {
    return "";
  }

  const normalizedValue = typeof value === "string" ? value.trim() : value;
  const parsed =
    typeof normalizedValue === "string" &&
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(normalizedValue)
      ? dayjs(normalizedValue, "YYYY-MM-DD HH:mm:ss", true)
      : dayjs(normalizedValue);

  if (!parsed.isValid()) {
    return value;
  }

  return new Intl.DateTimeFormat("pt-PT", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(parsed.toDate());
};

const LogsScreen = () => {
  const navigate = useNavigate();
  const user = auth.getCurrentUser();
  const userId = user?.data?.companyId || user?.data?.id;

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsLoadingMore, setLogsLoadingMore] = useState(false);
  const [logsError, setLogsError] = useState("");
  const [logsPage, setLogsPage] = useState(1);
  const [logsHasMore, setLogsHasMore] = useState(false);
  const [logsLoaded, setLogsLoaded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [logsFilters, setLogsFilters] = useState({
    action: "",
    entityType: "",
    logDate: "",
  });
  const [logDateValue, setLogDateValue] = useState(null);

  const mergeLogs = (currentLogs, incomingLogs) => {
    const seen = new Set(currentLogs.map((item) => String(item.id)));
    const appended = incomingLogs.filter((item) => !seen.has(String(item.id)));
    return [...currentLogs, ...appended];
  };

  const loadLogs = async ({
    reset = false,
    page = 1,
    filtersOverride = null,
  } = {}) => {
    if (!userId) {
      return;
    }

    const activeFilters = filtersOverride || logsFilters;

    try {
      setLogsError("");
      if (reset) {
        setLogsLoading(true);
      } else {
        setLogsLoadingMore(true);
      }

      const response = await getLogs({
        companyId: userId,
        limit: LOGS_PAGE_SIZE,
        page,
        action: activeFilters.action || undefined,
        entityType: activeFilters.entityType || undefined,
        logDate: activeFilters.logDate || undefined,
      });

      const incoming = Array.isArray(response?.data) ? response.data : [];
      setLogs((prev) => (reset ? incoming : mergeLogs(prev, incoming)));
      setLogsPage(page);
      setLogsHasMore(Boolean(response?.pagination?.hasMore));
      setLogsLoaded(true);
    } catch (error) {
      console.error("Error loading logs:", error);
      if (reset) {
        setLogs([]);
      }
      setLogsError("Não foi possível carregar os logs.");
    } finally {
      setLogsLoading(false);
      setLogsLoadingMore(false);
    }
  };

  const handleLogsFilterChange = (field) => (event) => {
    const value = event.target.value;
    setLogsFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogDateChange = (newValue) => {
    setLogDateValue(newValue);

    setLogsFilters((prev) => ({
      ...prev,
      logDate:
        newValue && dayjs(newValue).isValid()
          ? dayjs(newValue).format("YYYY-MM-DD")
          : "",
    }));
  };

  const handleApplyLogFilters = async () => {
    await loadLogs({ reset: true, page: 1 });
  };

  const handleClearLogFilters = async () => {
    const clearedFilters = { action: "", entityType: "", logDate: "" };
    setLogsFilters(clearedFilters);
    setLogDateValue(null);
    await loadLogs({ reset: true, page: 1, filtersOverride: clearedFilters });
  };

  const handleLoadMoreLogs = async () => {
    if (logsLoading || logsLoadingMore || !logsHasMore) {
      return;
    }

    await loadLogs({ reset: false, page: logsPage + 1 });
  };

  const handleLogsScroll = async (event) => {
    const node = event.currentTarget;
    const remaining = node.scrollHeight - node.scrollTop - node.clientHeight;

    if (remaining < 140) {
      await handleLoadMoreLogs();
    }
  };

  return (
    <Box
      sx={{ p: { xs: 2, md: 4 }, background: "#f7f5fb", minHeight: "100vh" }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          mb: 3,
          background: "linear-gradient(90deg, #5b2c72 0%, #7b1fa2 100%)",
          color: "white",
        }}
      >
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <FormatListBulleted />
              <Typography variant="h5" fontWeight="bold">
                Logs de atividade
              </Typography>
            </Stack>
            <Chip
              label="Voltar"
              color="secondary"
              onClick={() => navigate("/dashboard")}
              icon={<ArrowBack color="inherit" />}
            />
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 4 }}>
        <CardContent>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mb: 1.5 }}
          >
            <Button
              variant="outlined"
              onClick={() => setShowFilters((prev) => !prev)}
              startIcon={<FilterAlt />}
            >
              {showFilters ? "Ocultar filtros" : "Filtrar"}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => loadLogs({ reset: true, page: 1 })}
              startIcon={<RotateLeft />}
            >
              Atualizar
            </Button>
          </Stack>

          {showFilters && (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.2}
              sx={{ mb: 1.5 }}
            >
              <TextField
                select
                size="small"
                label="Ação"
                value={logsFilters.action}
                onChange={handleLogsFilterChange("action")}
                sx={{ minWidth: { md: 150 } }}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="login">Login</MenuItem>
                <MenuItem value="create">Criar</MenuItem>
                <MenuItem value="edit">Editar</MenuItem>
                <MenuItem value="upload">Carregar</MenuItem>
                <MenuItem value="delete">Apagar</MenuItem>
              </TextField>

              <TextField
                select
                size="small"
                label="Entidade"
                value={logsFilters.entityType}
                onChange={handleLogsFilterChange("entityType")}
                sx={{ minWidth: { md: 190 } }}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="user">Utilizador</MenuItem>
                <MenuItem value="client">Cliente</MenuItem>
                <MenuItem value="document">Documento</MenuItem>
                <MenuItem value="contract">Contrato</MenuItem>
                <MenuItem value="service">Serviço</MenuItem>
                <MenuItem value="company_access">Acesso da empresa</MenuItem>
                <MenuItem value="client_access">Acesso do cliente</MenuItem>
              </TextField>

              <DatePicker
                label="Data"
                value={logDateValue}
                onChange={handleLogDateChange}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { minWidth: { md: 170 } },
                  },
                }}
              />

              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={handleApplyLogFilters}>
                  Filtrar
                </Button>
                <Button onClick={handleClearLogFilters}>Limpar</Button>
              </Stack>
            </Stack>
          )}

          {logsError && (
            <Alert severity="error" sx={{ mb: 1.2 }}>
              {logsError}
            </Alert>
          )}

          {!logsLoaded && !logsLoading && (
            <Stack direction="row" justifyContent="center" sx={{ py: 4 }}>
              <Button
                color="secondary"
                variant="contained"
                onClick={() => loadLogs({ reset: true, page: 1 })}
                startIcon={<Downloading />}
              >
                Carregar logs
              </Button>
            </Stack>
          )}

          <Box
            onScroll={handleLogsScroll}
            sx={{ maxHeight: "65vh", overflowY: "auto", pr: 0.4 }}
          >
            <Stack spacing={1.2} sx={{ mt: 1 }}>
              {logsLoading ? (
                <Typography variant="body2" color="text.secondary">
                  A carregar logs...
                </Typography>
              ) : logs.length ? (
                logs.map((log) => (
                  <Card
                    key={log.id}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  >
                    <CardContent sx={{ py: 1.2, "&:last-child": { pb: 1.2 } }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        justifyContent="space-between"
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            label={
                              ACTION_LABELS_PT[log.action] || log.action || "-"
                            }
                            color={ACTION_CHIP_COLOR[log.action] || "secondary"}
                          />
                          <Chip
                            size="small"
                            label={
                              ENTITY_LABELS_PT[log.entityType] ||
                              log.entityType ||
                              "-"
                            }
                            variant="outlined"
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTimePt(log.createdAt)}
                        </Typography>
                      </Stack>

                      <Typography variant="body2" sx={{ mt: 0.8 }}>
                        Utilizador: {log.actorName || "Sem nome"} (
                        {log.actorType || "-"})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Empresa: {log.companyName || log.companyId || "-"} |
                        Cliente: {log.clientName || log.clientId || "-"}
                      </Typography>

                      {log.details && Object.keys(log.details).length ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          {Object.entries(log.details)
                            .slice(0, 6)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join(" | ")}
                        </Typography>
                      ) : null}
                    </CardContent>
                  </Card>
                ))
              ) : logsLoaded ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum log encontrado.
                </Typography>
              ) : null}

              {!logsLoading && logsLoadingMore && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ py: 0.5 }}
                >
                  <CircularProgress size={18} />
                  <Typography variant="caption" color="text.secondary">
                    A carregar mais logs...
                  </Typography>
                </Stack>
              )}

              {!logsLoading && !logsLoadingMore && logsHasMore && (
                <Button
                  onClick={handleLoadMoreLogs}
                  sx={{ alignSelf: "center" }}
                >
                  Carregar mais
                </Button>
              )}
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LogsScreen;
